import { FC, PointerEvent as ReactPointerEvent, useRef, useState } from 'react';

import {
  ThanksPrintBox,
  ThanksPrintTextAlign,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';

export type ThanksPrintBoxKey = 'message' | 'sender';

interface ThanksPrintBoxEditorProps {
  /** 템플릿 이미지 URL (object URL 또는 버킷 URL) */
  imageUrl: string;
  messageBox?: ThanksPrintBox;
  senderBox?: ThanksPrintBox;
  /** 박스 좌표(0~1) 변경 콜백 */
  onChange: (key: ThanksPrintBoxKey, box: ThanksPrintBox) => void;
}

type Corner = 'nw' | 'ne' | 'sw' | 'se';

type DragState =
  | { type: 'move'; key: ThanksPrintBoxKey; startX: number; startY: number; orig: ThanksPrintBox }
  | { type: 'resize'; key: ThanksPrintBoxKey; corner: Corner; orig: ThanksPrintBox }
  | { type: 'draw'; key: ThanksPrintBoxKey; anchorX: number; anchorY: number };

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** x0<x1, y0<y1 이 되도록 정렬 (align은 보존) */
const normalizeBox = (box: ThanksPrintBox): ThanksPrintBox => ({
  x0: Math.min(box.x0, box.x1),
  y0: Math.min(box.y0, box.y1),
  x1: Math.max(box.x0, box.x1),
  y1: Math.max(box.y0, box.y1),
  align: box.align,
});

const DEFAULT_BOX: Record<ThanksPrintBoxKey, ThanksPrintBox> = {
  message: { x0: 0.15, y0: 0.2, x1: 0.85, y1: 0.5 },
  sender: { x0: 0.55, y0: 0.6, x1: 0.9, y1: 0.72 },
};

const BOX_META: Record<
  ThanksPrintBoxKey,
  { label: string; border: string; bg: string; badge: string; handle: string }
> = {
  message: {
    label: '감사글',
    border: 'border-indigo-500',
    bg: 'bg-indigo-500/15',
    badge: 'bg-indigo-500',
    handle: 'bg-indigo-500',
  },
  sender: {
    label: '상주명',
    border: 'border-rose-500',
    bg: 'bg-rose-500/15',
    badge: 'bg-rose-500',
    handle: 'bg-rose-500',
  },
};

const CORNERS: { corner: Corner; pos: string }[] = [
  { corner: 'nw', pos: '-left-1.5 -top-1.5 cursor-nwse-resize' },
  { corner: 'ne', pos: '-right-1.5 -top-1.5 cursor-nesw-resize' },
  { corner: 'sw', pos: '-left-1.5 -bottom-1.5 cursor-nesw-resize' },
  { corner: 'se', pos: '-right-1.5 -bottom-1.5 cursor-nwse-resize' },
];

export const ThanksPrintBoxEditor: FC<ThanksPrintBoxEditorProps> =
  function ThanksPrintBoxEditor({ imageUrl, messageBox, senderBox, onChange }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<DragState | null>(null);
    const [activeKey, setActiveKey] = useState<ThanksPrintBoxKey>('message');

    const boxes: Record<ThanksPrintBoxKey, ThanksPrintBox | undefined> = {
      message: messageBox,
      sender: senderBox,
    };

    // 좌표 변경 시 align 유지 — coords에 있으면 우선, 없으면 현재 박스값, 그래도 없으면 기본(center)
    const emit = (key: ThanksPrintBoxKey, coords: ThanksPrintBox) => {
      onChange(key, {
        ...coords,
        align: coords.align ?? boxes[key]?.align ?? ThanksPrintTextAlign.Center,
      });
    };

    // 포인터 위치를 컨테이너 기준 0~1 비율로 변환
    const toNorm = (e: ReactPointerEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return {
        x: clamp01((e.clientX - rect.left) / rect.width),
        y: clamp01((e.clientY - rect.top) / rect.height),
      };
    };

    // 빈 영역 클릭 → 활성 박스 새로 그리기 시작
    const onCanvasPointerDown = (e: ReactPointerEvent) => {
      // 박스/핸들에서 시작한 경우는 각자 핸들러가 처리(stopPropagation)
      const { x, y } = toNorm(e);
      containerRef.current?.setPointerCapture(e.pointerId);
      dragRef.current = { type: 'draw', key: activeKey, anchorX: x, anchorY: y };
      emit(activeKey, { x0: x, y0: y, x1: x, y1: y });
    };

    const onBoxPointerDown = (e: ReactPointerEvent, key: ThanksPrintBoxKey) => {
      e.stopPropagation();
      const box = boxes[key];
      if (!box) return;
      setActiveKey(key);
      const { x, y } = toNorm(e);
      containerRef.current?.setPointerCapture(e.pointerId);
      dragRef.current = {
        type: 'move',
        key,
        startX: x,
        startY: y,
        orig: normalizeBox(box),
      };
    };

    const onHandlePointerDown = (
      e: ReactPointerEvent,
      key: ThanksPrintBoxKey,
      corner: Corner,
    ) => {
      e.stopPropagation();
      const box = boxes[key];
      if (!box) return;
      setActiveKey(key);
      containerRef.current?.setPointerCapture(e.pointerId);
      dragRef.current = { type: 'resize', key, corner, orig: normalizeBox(box) };
    };

    const onPointerMove = (e: ReactPointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const { x, y } = toNorm(e);

      if (drag.type === 'draw') {
        emit(drag.key, { x0: drag.anchorX, y0: drag.anchorY, x1: x, y1: y });
        return;
      }

      if (drag.type === 'move') {
        const w = drag.orig.x1 - drag.orig.x0;
        const h = drag.orig.y1 - drag.orig.y0;
        const dx = x - drag.startX;
        const dy = y - drag.startY;
        const nx0 = Math.min(Math.max(drag.orig.x0 + dx, 0), 1 - w);
        const ny0 = Math.min(Math.max(drag.orig.y0 + dy, 0), 1 - h);
        emit(drag.key, { x0: nx0, y0: ny0, x1: nx0 + w, y1: ny0 + h });
        return;
      }

      // resize: 잡은 코너만 이동, 반대 코너 고정 (align 보존)
      const o = drag.orig;
      const left = drag.corner === 'nw' || drag.corner === 'sw' ? x : o.x0;
      const right = drag.corner === 'ne' || drag.corner === 'se' ? x : o.x1;
      const top = drag.corner === 'nw' || drag.corner === 'ne' ? y : o.y0;
      const bottom = drag.corner === 'sw' || drag.corner === 'se' ? y : o.y1;
      onChange(drag.key, { x0: left, y0: top, x1: right, y1: bottom, align: o.align });
    };

    const onPointerUp = (e: ReactPointerEvent) => {
      const drag = dragRef.current;
      if (drag) {
        const box = boxes[drag.key];
        if (box) emit(drag.key, normalizeBox(box));
      }
      dragRef.current = null;
      containerRef.current?.releasePointerCapture(e.pointerId);
    };

    const renderBox = (key: ThanksPrintBoxKey) => {
      const raw = boxes[key];
      if (!raw) return null;
      const box = normalizeBox(raw);
      const meta = BOX_META[key];
      const isActive = key === activeKey;
      return (
        <div
          key={key}
          onPointerDown={(e) => onBoxPointerDown(e, key)}
          className={`absolute border-2 ${meta.border} ${meta.bg} ${
            isActive ? 'z-20' : 'z-10 opacity-60'
          }`}
          style={{
            left: `${box.x0 * 100}%`,
            top: `${box.y0 * 100}%`,
            width: `${(box.x1 - box.x0) * 100}%`,
            height: `${(box.y1 - box.y0) * 100}%`,
            cursor: 'move',
          }}
        >
          <span
            className={`absolute -top-5 left-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold text-white ${meta.badge}`}
          >
            {meta.label}
          </span>
          {isActive &&
            CORNERS.map(({ corner, pos }) => (
              <div
                key={corner}
                onPointerDown={(e) => onHandlePointerDown(e, key, corner)}
                className={`absolute h-3 w-3 rounded-full border-2 border-white ${meta.handle} ${pos}`}
              />
            ))}
        </div>
      );
    };

    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        {/* 이미지 + 박스 오버레이 (왼쪽, 적당한 크기로 제한) */}
        <div
          ref={containerRef}
          onPointerDown={onCanvasPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className="relative w-full shrink-0 touch-none select-none overflow-hidden rounded-lg border border-gray-200 bg-gray-50 sm:w-80"
        >
          <img
            src={imageUrl}
            alt="감사인쇄 템플릿"
            draggable={false}
            className="pointer-events-none w-full select-none"
          />
          {renderBox('sender')}
          {renderBox('message')}
        </div>

        {/* 컨트롤 (오른쪽, 남는 공간 활용) */}
        <div className="min-w-0 flex-1 space-y-3">
          {/* 박스 선택 탭 */}
          <div className="flex flex-wrap gap-2">
            {(['message', 'sender'] as ThanksPrintBoxKey[]).map((key) => {
              const meta = BOX_META[key];
              const isActive = key === activeKey;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveKey(key)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                    isActive
                      ? `${meta.border} ${meta.badge} text-white`
                      : 'border-gray-300 bg-white text-gray-600'
                  }`}
                >
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      isActive ? 'bg-white' : meta.badge
                    }`}
                  />
                  {meta.label} 영역
                  {!boxes[key] && <span className="opacity-70">(미설정)</span>}
                </button>
              );
            })}
          </div>

          {/* 활성 박스 정렬 */}
          {boxes[activeKey] && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">정렬</span>
              <div className="flex overflow-hidden rounded-md border border-gray-300">
                {(
                  [
                    { value: ThanksPrintTextAlign.Left, label: '왼쪽' },
                    { value: ThanksPrintTextAlign.Center, label: '가운데' },
                    { value: ThanksPrintTextAlign.Right, label: '오른쪽' },
                  ] as const
                ).map(({ value, label }) => {
                  const current = boxes[activeKey]?.align ?? ThanksPrintTextAlign.Center;
                  const isActive = current === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => {
                        const box = boxes[activeKey];
                        if (box) onChange(activeKey, { ...box, align: value });
                      }}
                      className={`px-2.5 py-1 text-xs font-medium ${
                        isActive ? 'bg-gray-800 text-white' : 'bg-white text-gray-600'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* 안내 + 기본 위치 */}
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs text-gray-500">
              이미지 위에서 <b>드래그</b>해 <b>{BOX_META[activeKey].label}</b> 영역을
              그리고, 모서리 점으로 크기를 조절하세요.
            </p>
            <button
              type="button"
              onClick={() => emit(activeKey, DEFAULT_BOX[activeKey])}
              className="shrink-0 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
            >
              기본 위치
            </button>
          </div>
        </div>
      </div>
    );
  };
